(() => {
  const SECTION_SELECTOR = '.premium-contact-page[data-contact-page-root]';
  const initialized = new WeakSet();

  const toFontStack = (font) => {
    if (!font || !font.family) return 'inherit';
    const family = String(font.family).replace(/"/g, '\\"');
    const fallbacks = font.fallbacks ? String(font.fallbacks) : '';
    const fallbackStack = fallbacks
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .join(', ');
    return fallbackStack ? `"${family}", ${fallbackStack}` : `"${family}"`;
  };

  const setVar = (root, name, value) => {
    if (value === undefined || value === null || value === '') return;
    root.style.setProperty(name, String(value));
  };

  const initSection = (section) => {
    if (!section || initialized.has(section)) return;

    const settingsEl = section.querySelector('.premium-contact-page__settings');
    if (!settingsEl) return;

    let settings;
    try {
      settings = JSON.parse(settingsEl.textContent);
    } catch (error) {
      return;
    }

    initialized.add(section);

    const applySettings = () => {
      const width = window.innerWidth;
      const desktopBreakpoint = Number(settings.desktopBreakpoint || 990);

      const mode = width < desktopBreakpoint ? 'mobile' : 'desktop';
      section.classList.toggle('is-mobile', mode === 'mobile');
      section.classList.toggle('is-desktop', mode === 'desktop');

      const tabletThreshold = 768;
      const sectionPaddingX =
        width < tabletThreshold
          ? settings.mobilePaddingX
          : width < desktopBreakpoint
            ? settings.tabletPaddingX
            : settings.desktopPaddingX;

      setVar(section, '--pcp-page-bg', settings.pageBackground);
      setVar(section, '--pcp-section-bg', settings.sectionBackground);
      setVar(section, '--pcp-content-bg', settings.contentBackground);
      setVar(section, '--pcp-form-bg', settings.formBackground);
      setVar(section, '--pcp-help-bg', settings.helpBackground);

      setVar(section, '--pcp-bg-overlay', settings.overlayColor);
      setVar(section, '--pcp-bg-overlay-opacity', settings.overlayOpacity);
      setVar(section, '--pcp-bg-image', settings.backgroundImage ? `url("${settings.backgroundImage}")` : 'none');
      setVar(section, '--pcp-bg-position', settings.backgroundPosition);
      setVar(section, '--pcp-bg-size', settings.backgroundSize);
      setVar(section, '--pcp-bg-repeat', settings.backgroundRepeat);
      setVar(section, '--pcp-bg-opacity', 1);

      setVar(section, '--pcp-max-width', `${settings.maxWidth}px`);
      setVar(section, '--pcp-section-padding-x', `${sectionPaddingX}px`);
      setVar(section, '--pcp-section-padding-mobile', `${settings.mobilePaddingX}px`);
      setVar(section, '--pcp-top-padding', `${settings.topPadding}px`);
      setVar(section, '--pcp-bottom-padding', `${settings.bottomPadding}px`);
      setVar(section, '--pcp-column-gap', `${settings.columnGap}px`);
      setVar(section, '--pcp-field-gap', `${settings.fieldGap}px`);
      setVar(section, '--pcp-form-width', `${settings.formWidth}%`);
      setVar(section, '--pcp-panel-width', `${settings.panelWidth}%`);
      setVar(section, '--pcp-divider-width', `${settings.dividerWidth}px`);

      setVar(section, '--pcp-heading-font', toFontStack(settings.headingFont));
      setVar(section, '--pcp-body-font', toFontStack(settings.bodyFont));
      setVar(section, '--pcp-label-font', toFontStack(settings.labelFont));
      setVar(section, '--pcp-breadcrumb-font', toFontStack(settings.breadcrumbFont));
      setVar(section, '--pcp-button-font', toFontStack(settings.buttonFont));

      setVar(section, '--pcp-heading-size', `${settings.headingSize}px`);
      setVar(section, '--pcp-body-size', `${settings.bodySize}px`);
      setVar(section, '--pcp-label-size', `${settings.labelSize}px`);
      setVar(section, '--pcp-breadcrumb-size', `${settings.breadcrumbSize}px`);
      setVar(section, '--pcp-button-size', `${settings.buttonSize}px`);

      setVar(section, '--pcp-heading-weight', settings.headingWeight);
      setVar(section, '--pcp-body-weight', settings.bodyWeight);
      setVar(section, '--pcp-label-weight', settings.labelWeight);
      setVar(section, '--pcp-breadcrumb-weight', settings.breadcrumbWeight);
      setVar(section, '--pcp-button-weight', settings.buttonWeight);

      setVar(section, '--pcp-heading-line-height', settings.headingLineHeight);
      setVar(section, '--pcp-body-line-height', settings.bodyLineHeight);
      setVar(section, '--pcp-label-line-height', settings.labelLineHeight);
      setVar(section, '--pcp-breadcrumb-line-height', settings.breadcrumbLineHeight);
      setVar(section, '--pcp-button-line-height', settings.buttonLineHeight);

      setVar(section, '--pcp-heading-letter-spacing', `${settings.headingLetterSpacing}px`);
      setVar(section, '--pcp-body-letter-spacing', `${settings.bodyLetterSpacing}px`);
      setVar(section, '--pcp-label-letter-spacing', `${settings.labelLetterSpacing}px`);
      setVar(section, '--pcp-breadcrumb-letter-spacing', `${settings.breadcrumbLetterSpacing}px`);
      setVar(section, '--pcp-button-letter-spacing', `${settings.buttonLetterSpacing}px`);

      setVar(section, '--pcp-heading-color', settings.headingColor);
      setVar(section, '--pcp-body-color', settings.bodyColor);
      setVar(section, '--pcp-label-color', settings.labelColor);
      setVar(section, '--pcp-placeholder-color', settings.placeholderColor);
      setVar(section, '--pcp-breadcrumb-color', settings.breadcrumbColor);
      setVar(section, '--pcp-breadcrumb-link-color', settings.breadcrumbLinkColor);
      setVar(section, '--pcp-breadcrumb-hover-color', settings.breadcrumbHoverColor);
      setVar(section, '--pcp-breadcrumb-separator-color', settings.breadcrumbSeparatorColor);
      setVar(section, '--pcp-divider-color', settings.dividerColor);
      setVar(section, '--pcp-icon-color', settings.iconColor);
      setVar(section, '--pcp-help-title-color', settings.helpTitleColor);
      setVar(section, '--pcp-help-text-color', settings.helpTextColor);

      setVar(section, '--pcp-input-bg', settings.inputBg);
      setVar(section, '--pcp-input-border-color', settings.inputBorder);
      setVar(section, '--pcp-input-focus-border', settings.inputFocusBorder);
      setVar(section, '--pcp-input-text', settings.inputText);
      setVar(section, '--pcp-input-height', `${settings.inputHeight}px`);
      setVar(section, '--pcp-textarea-height', `${settings.textareaHeight}px`);
      setVar(section, '--pcp-input-radius', `${settings.inputRadius}px`);
      setVar(section, '--pcp-input-border-width', `${settings.inputBorderWidth}px`);
      setVar(section, '--pcp-input-padding-x', `${settings.inputPaddingX}px`);
      setVar(section, '--pcp-input-padding-y', `${settings.inputPaddingY}px`);

      setVar(section, '--pcp-button-text', settings.buttonText);
      setVar(section, '--pcp-button-bg', settings.buttonBg);
      setVar(section, '--pcp-button-border', settings.buttonBorder);
      setVar(section, '--pcp-button-hover-text', settings.buttonHoverText);
      setVar(section, '--pcp-button-hover-bg', settings.buttonHoverBg);
      setVar(section, '--pcp-button-hover-border', settings.buttonHoverBorder);
      setVar(section, '--pcp-button-radius', `${settings.buttonRadius}px`);
      setVar(section, '--pcp-button-padding-x', `${settings.buttonPaddingX}px`);
      setVar(section, '--pcp-button-padding-y', `${settings.buttonPaddingY}px`);
      setVar(section, '--pcp-button-border-width', `${settings.buttonBorderWidth}px`);
      setVar(section, '--pcp-button-transform', settings.buttonUppercase ? 'uppercase' : 'none');
      setVar(section, '--pcp-button-transition', '280ms');
      setVar(section, '--pcp-button-shadow', settings.buttonShadow ? '0 10px 24px rgba(0,0,0,0.08)' : 'none');

      setVar(section, '--pcp-secondary-button-text', settings.secondaryButtonText);
      setVar(section, '--pcp-secondary-button-bg', settings.secondaryButtonBg);
      setVar(section, '--pcp-secondary-button-border', settings.secondaryButtonBorder);
      setVar(section, '--pcp-secondary-button-hover-text', settings.secondaryButtonHoverText);
      setVar(section, '--pcp-secondary-button-hover-bg', settings.secondaryButtonHoverBg);
      setVar(section, '--pcp-secondary-button-hover-border', settings.secondaryButtonHoverBorder);

      setVar(section, '--pcp-icon-size', `${settings.iconSize}px`);
    };

    applySettings();

    let resizeTimer = null;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(applySettings, 60);
    };

    window.addEventListener('resize', onResize);

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target !== section) return;
      window.removeEventListener('resize', onResize);
    });
  };

  const initAll = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector(SECTION_SELECTOR);

    if (section) initSection(section);
  });

  if (document.readyState !== 'loading') {
    initAll();
  }
})();